import { useState } from "react";
import { useTranslation } from "react-i18next";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as yup from "yup";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

import AuthService from "services/http/AuthService";
import ViewContainer from "components/ViewContainer/ViewContainer";
import ButtonPrimary from "components/Buttons/ButtonPrimary";
import MyTextField from "components/Inputs/MyTextField";

const auth = new AuthService();

const styles = (theme) => ({
  forgotLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

const useStyles = makeStyles(styles);

const Login = (_props) => {
  const classes = useStyles();

  const [values, setValues] = useState({
    username: "",
    password: "",
  });

  const [errors, setErrors] = useState(false);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleInputChange = (event) => {
    let { value, name } = event.target;

    if (["username"].indexOf(name) > -1) {
      value = value.toLowerCase();
    }

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validationSchema = yup.object().shape({
    username: yup.string().required(t("Pakollinen kenttä")),
    password: yup.string().required(t("Pakollinen kenttä")),
  });

  const validateForm = () => {
    return new Promise((resolve, reject) => {
      validationSchema
        .validate(values, { abortEarly: true })
        .then(() => {
          // GOOD
          setErrors({});
          resolve(true);
        })
        .catch((_err) => {
          // INVALID
          setErrors(true);
          reject("NO_GOOD");
        });
    });
  };

  const onSubmit = (event) => {
    event.preventDefault();

    validateForm().then(
      (_response) => {
        handleLogin();
      },
      (errors) => {
        console.log("validateForm errors", errors);
      },
    );
  };

  const handleLogin = () => {
    setSaving(true);
    const data = {
      username: values.username.toLowerCase(),
      password: values.password,
    };
    auth.login(data.username, data.password).then(
      (_response) => {
        setSaving(false);
        navigate("/dashboard/start");
      },
      (_error) => {
        setErrors(true);
        setSaving(false);
      },
    );
  };

  return (
    <ViewContainer title={t("Kirjaudu sisään saamillasi tunnuksilla")}>
      <form className={classes.root} onSubmit={onSubmit}>
        <Grid container className={classes.container}>
          <Grid size={{ xs: 12 }}>
            <MyTextField
              customLabel={t("Sähköposti / käyttäjätunnus")}
              id="login-username"
              name="username"
              value={values.username}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <MyTextField
              autoComplete="current-password"
              customLabel={t("Salasana")}
              id="login-password"
              name="password"
              type="password"
              value={values.password}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <RouterLink to="/auth/reset" className={classes.forgotLink}>
              <Typography variant="subtitle2">
                {t("Salasana unohtunut?")}
              </Typography>
            </RouterLink>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Grid
              container
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
            >
              <Grid size={12}></Grid>
              <Grid size={12}>
                <ButtonPrimary loading={saving} type="submit">
                  {t("Kirjaudu")}
                </ButtonPrimary>
              </Grid>
            </Grid>
          </Grid>

          {errors === true ? (
            <Typography variant="subtitle2" color="error" gutterBottom>
              {t(
                "Kirjautuminen epäonnistui. Tarkista käyttäjätunnus ja salasana.",
              )}
            </Typography>
          ) : null}
        </Grid>
      </form>
    </ViewContainer>
  );
};

export default Login;
